import { Suspense, lazy, useMemo } from 'react'
import { MDXProvider } from '@mdx-js/react'
import { Link, useParams } from 'react-router'
import { Section, SectionWrapper } from '@/components/layout/Section'
import { BottomCTA } from '@/components/sections'
import { Prose } from '@/components/content/Prose'
import { mdxComponents } from '@/components/content/mdx-components'
import { SUPPORT_DOCS } from '@/content/docs.generated'
import { loadDoc } from '@/content/loader'
import { NotFound } from '@/routes/NotFound'
import { groupLabel } from '@/content/support-groups'
import { Seo } from '@/components/seo/Seo'
import { BreadcrumbJsonLd } from '@/components/seo/JsonLd'

/**
 * `/support/:group/:slug` — one template for all 23 support documents,
 * with an in-group sidebar so a reader can move between siblings.
 */
export function SupportDocPage() {
  const { group, slug } = useParams()
  const doc = SUPPORT_DOCS.find((d) => d.slug === `${group}/${slug}`)
  const siblings = SUPPORT_DOCS.filter((d) => d.category === group)

  const Body = useMemo(() => {
    if (!doc) return null
    const loader = loadDoc(doc.route)
    return loader ? lazy(async () => ({ default: (await loader()).default })) : null
  }, [doc])

  if (!doc) return <NotFound />

  return (
    <>
      <Seo
        title={doc.seoTitle}
        description={doc.description}
        path={doc.route}
        image={doc.ogImage}
      />
      <BreadcrumbJsonLd
        items={[
          { name: 'Support', path: '/support' },
          { name: groupLabel(doc.category), path: '/support' },
          { name: doc.title, path: doc.route },
        ]}
      />
      <Section surface="gray" padding="lg" paddingTop="3xl" paddingBottom="xl" className="pb-12">
        <SectionWrapper layout="stack" width="content">
          <nav aria-label="Breadcrumb" className="font-mono text-xs tracking-label uppercase">
            <Link to="/support" className="text-blue no-underline hover:underline">
              Support
            </Link>
            <span className="text-font-secondary"> / {groupLabel(doc.category)}</span>
          </nav>
          <h1 className="text-center">{doc.title}</h1>
          {doc.description && (
            <p className="text-font-secondary">{doc.description}</p>
          )}
        </SectionWrapper>
      </Section>

      <Section padding="lg" className="pt-12">
        <SectionWrapper layout="row" className="flex-col lg:flex-row lg:items-start">
          {siblings.length > 1 && (
            <aside className="w-full flex-none lg:sticky lg:top-24 lg:w-[240px]">
              <h6 className="m-0 meta-label text-font-secondary">
                {groupLabel(doc.category)}
              </h6>
              <ul className="flex list-none flex-col gap-2 p-0">
                {siblings.map((s) => (
                  <li key={s.slug} className="m-0">
                    <Link
                      to={s.route}
                      className={
                        s.slug === doc.slug
                          ? 'text-small font-medium text-blue no-underline'
                          : 'text-small text-font-secondary no-underline hover:text-font-default'
                      }
                      aria-current={s.slug === doc.slug ? 'page' : undefined}
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          )}
          <div className="min-w-0 flex-1 max-w-content">
            <MDXProvider components={mdxComponents}>
              <Prose>
                <Suspense
                  fallback={<p className="font-mono text-sm text-font-secondary">Loading…</p>}
                >
                  {Body && <Body />}
                </Suspense>
              </Prose>
            </MDXProvider>
          </div>
        </SectionWrapper>
      </Section>

      <BottomCTA />
    </>
  )
}
